import {
  slab, wallX, wallZ, block, goal, sign, neon, checkpoint, edgeLights,
  sentinel, crusher, boost,
} from '../kit.js';

/**
 * CHAPTER 2, SECTOR 17: WATCHTOWER. The anti-linger sector.
 *
 * Every other sector rewards thinking on the spot. This one punishes it:
 * sentinels sweep the open spans, so cover-to-cover momentum IS survival
 * and hesitation spirals (slow means seen, seen means slower).
 *
 * The shock debuff is load bearing, not decoration:
 *   - move speed halves, so a shocked crossing takes twice the exposure
 *   - jump velocity drops to sqrt(1/2), which is HALF the jump HEIGHT:
 *     1.70m unshocked, 0.85m shocked (jumpVelocity 9.4, gravity 26)
 * Chapter 2's stair rises 1.3m per step, deliberately between those two
 * numbers. An unshocked courier climbs it at a run; a shocked one
 * physically cannot leave the step they are standing on until it wears
 * off, in full view of the thing that shocked them. The sector's whole
 * thesis, expressed as arithmetic.
 *
 * Sentinel geometry rules used here: the eye sits 1.0 above its mount and
 * needs unbroken line of sight for `lock` seconds; cover has to be TALLER
 * than the eye to work, so every mount is low and every cover piece is
 * ten metres of solid plate.
 */
const pillar = (x, z) => block(x, 0, z, 3, 10, 3, 'trim');

export default {
  id: 'watchtower',
  name: 'WATCHTOWER',
  blurb: 'They still watch. Keep moving.',
  mode: { type: 'sprint', par: 170 },
  // REDLINE: every sentinel locks at the spec's 1.4s (the seam-fix round
  // slowed the stair's to 2.6s while a crawlspace bug was still being
  // read as difficulty); reaches stay as authored, since the yard's and
  // the bridge's are already longer than the spec's 26m. Clock: 85s,
  // twice the record line.
  redline: { seconds: 85, tune: { sentinel: { lock: 1.4 } } },
  spawn: { p: [0, 0.4, 10], yaw: 0 },
  killY: -40,
  env: {
    fog: 0x0a1014,
    fogDensity: 0.0042,
    sky: { zenith: 0x03070a, horizon: 0x0d1a20, glow: 0xb85a1a, aurora: 0xffb03c },
    floorY: -260,
  },
  objects: [
    // ---- chapter 1: the yard. Watchers line both walls and the pillars
    // are offset row to row, so the field is a lattice of short cover
    // pockets rather than clean lanes. The lock is 3.0s, about 34 metres
    // of sprint: measured, that is the longest exposed stretch a good
    // line through the pockets actually incurs (a 2.4s lock caught the
    // scripted line once, at -13,-24, crossing between two pockets). A
    // body that stops anywhere in the open still hits it with room over.
    slab(0, 0, -26, 42, 76, 'plate', 1.4),
    ...edgeLights(0, 0, -26, 42, 76, 'amber'),
    wallX(-21.7, 0, -26, 76, 11, 'plate', 1.4),
    wallX(21.7, 0, -26, 76, 11, 'plate', 1.4),
    wallZ(0, 0, 12.7, 43, 11, 'plate', 1.4),

    pillar(-15, -6), pillar(-5, -6), pillar(5, -6), pillar(15, -6),
    pillar(-10, -16), pillar(0, -16), pillar(10, -16), pillar(-20, -16), pillar(20, -16),
    pillar(-15, -26), pillar(-5, -26), pillar(5, -26), pillar(15, -26),
    pillar(-10, -36), pillar(0, -36), pillar(10, -36), pillar(-20, -36), pillar(20, -36),
    pillar(-15, -46), pillar(-5, -46), pillar(5, -46), pillar(15, -46),

    // Mounted along BOTH side walls, alternating, so the yard is watched
    // the whole way down instead of only at its far end. Measured first
    // cut had every sentinel on the far wall: the first forty metres were
    // a stroll and the last sixteen were seen by all three at once.
    sentinel(-20.4, 0, -8, 30, 3.0, 3),
    sentinel(20.4, 0, -22, 30, 3.0, 3),
    sentinel(-20.4, 0, -36, 30, 3.0, 3),
    // Off the centre line on purpose. Dead ahead of the doorway it flanks,
    // this one held a clear firing lane down the exit corridor; from here
    // the wall it stands beside blocks that shot, so the doorway reads as
    // an escape rather than a last toll.
    sentinel(-14, 0, -58, 26, 3.0, 3),
    neon(0, 0.1, -60, [40, 0.12, 0.12], 'amber', { light: true, intensity: 18, range: 26 }),

    sign(0, 3.4, 6, 0, [
      'WATCHTOWER',
      'THEY LOCK ON WHAT HOLDS STILL',
      'COVER TO COVER, DO NOT STOP',
    ]),
    sign(-16, 2.6, -12, 0, ['AMBER IS LOOKING', 'RED IS FIRING'], '#ffb03c'),

    // The yard's exit is a doorway in the sentinel wall, off the centre
    // line: reaching it is itself a crossing.
    wallZ(-13, 0, -63, 17, 11, 'plate', 1.4),
    wallZ(13, 0, -63, 17, 11, 'plate', 1.4),
    // The link corridor. Without it the yard floor stopped at z -64 and the
    // stair deck began at -75: an eleven metre gap against an 8.2m maximum
    // sprinting jump, with the checkpoint floating inside it. Reported from
    // play; the chapter probes all teleported past it.
    slab(0, 0, -70, 11, 14, 'plate', 1.4),
    ...edgeLights(0, 0, -70, 11, 14, 'amber'),
    checkpoint(0, 0, -68),
    sign(6, 2.6, -70, 0, [
      'COURIER LOG 04',
      'THEY STILL WATCH',
      'I NO LONGER THINK ANYONE READS THE REPORTS',
    ], '#ffb03c'),

    // ---- chapter 2: the stair. A spiral around a solid core, rising
    // 1.3m a step: a run clears it, a shocked jump (0.85m) cannot. The
    // core is the only cover, so each watcher owns a third of the climb
    // and the safe line is to keep turning rather than keep climbing.
    slab(0, 0, -92, 34, 34, 'plate', 1.4),
    ...edgeLights(0, 0, -92, 34, 34, 'amber'),
    block(0, 0, -92, 11, 26, 11, 'plate'),
    neon(0, 12, -86.4, [9, 0.12, 0.12], 'amber'),

    // Steps are 9x9 on a 9-unit spiral, which makes consecutive treads
    // TOUCH. At 7x7 every corner left a 2m diagonal gap and the climb was
    // impossible: reported from play, then confirmed by a shock-immune
    // walker that fell back to the deck at the fourth tread.
    slab(9, 1.3, -85, 9, 9, 'plate', 1.0),
    slab(9, 2.6, -92, 9, 9, 'plate', 1.0),
    slab(9, 3.9, -99, 9, 9, 'plate', 1.0),
    slab(0, 5.2, -101, 9, 9, 'plate', 1.0),
    slab(-9, 6.5, -99, 9, 9, 'plate', 1.0),
    slab(-9, 7.8, -92, 9, 9, 'plate', 1.0),
    slab(-9, 9.1, -85, 9, 9, 'plate', 1.0),
    slab(0, 10.4, -83, 9, 9, 'plate', 1.0),
    slab(9, 11.7, -85, 9, 9, 'plate', 1.0),
    slab(9, 13, -92, 9, 9, 'plate', 1.0),
    slab(9, 14.3, -99, 9, 9, 'plate', 1.0),
    slab(0, 15.6, -101, 9, 9, 'plate', 1.0),
    slab(-9, 16.9, -99, 9, 9, 'plate', 1.0),
    slab(-9, 18.2, -92, 9, 9, 'plate', 1.0),

    // One per face per third of the climb, placed against the measured
    // exposure map: the core shadows the opposite side, so the safe line
    // is to keep turning rather than to keep climbing one face.
    // Two watchers, not three, each with a short reach so it owns its own
    // stretch of the climb and the treads between them are genuine
    // recovery. The 1.3m rise means a shock STOPS a climber, so a stair
    // that is watched end to end is not a test, it is a wait: measured in
    // play, the only way up was to eat a shot and time the next hop
    // against the debuff wearing off.
    sentinel(16, 4, -92, 18, 2.6, 3),
    sentinel(-16, 11, -92, 18, 2.6, 3),
    sign(9, 3.0, -78, 0, ['THE STAIR', 'SHOCKED LEGS DO NOT CLIMB'], '#ffb03c'),
    checkpoint(-9, 18.2, -92),

    // ---- chapter 3: the gauntlet bridge. It runs WEST off the stair
    // top, not north: routed north it sat directly over the upper treads,
    // roofing tread twelve with 1.3m of clearance so the climb dead-ended
    // in a crawlspace. Reported from play as an impossible jump, and a
    // shock-immune walker stalled in exactly the same place.
    //
    // One exposed span, a sentinel dead ahead, and a boost strip as the
    // only way to cross inside its lock: 34m at the strip's measured
    // ~23 m/s is 1.5s against a 2.2s lock, where a 7.6 m/s walk takes 4.5s
    // and is seen twice over. The crusher at the gate is the last
    // chokepoint, and a shocked courier is too slow for its dwell.
    slab(-20, 18.2, -92, 14, 10, 'plate', 1.2),
    ...edgeLights(-20, 18.2, -92, 14, 10, 'amber'),
    boost(-26, 18.2, -92, 12, 5, [-1, 0, 0], 40, 40),
    slab(-46, 18.2, -92, 34, 7, 'plate', 1.2),
    ...edgeLights(-46, 18.2, -92, 34, 7, 'amber'),
    sentinel(-70, 18.4, -92, 44, 2.2, 3),

    slab(-73, 18.2, -92, 20, 16, 'plate', 1.2),
    ...edgeLights(-73, 18.2, -92, 20, 16, 'lime'),
    wallX(-83.6, 18.2, -92, 16, 8, 'plate', 1.2),
    crusher(-66, 24.2, -92, [-66, 19.3, -92], [4, 2, 8], 3.4, 0),
    sign(-30, 21.4, -92, 90, ['THE CROSSING', 'RIDE THE STRIP', 'WALKING IS SEEN'], '#ffb03c'),
    goal(-76, 18.2, -92, 90),
  ],
};
